import React, { ChangeEvent, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IBaseComponent } from '../../sharedInterfaces/IBaseComponent';
import { setLoanPurpose, setLoanPurposeAdditionalInfo } from '~/actions/formActions';
import { setMissingError } from '~/actions/errorActions';
import { LOAN_PURPOSE } from '~/actions/actionTypes';
import DropDown from '../Base/DropDown/DropDown';
import TextArea from '../Base/TextArea/TextArea';
import Input from '../Base/Input/Input';
import ComponentError from '../ComponentError/ComponentError';
import { IState } from '~/sharedInterfaces/IState';
import { IBaseInput, IDropDownOption } from '~/sharedInterfaces/SharedInterfaces';
import { getLoanPurposeLabels } from '~/services/DefaultLabelNameService';
import { pushToGTM } from '~/actions/gtmActions';

export enum LoanPurposeComponentTypes {
  dropDown = 'dropDown',
  textArea = 'textArea',
  input = 'input',
}

export interface ILoanPurpose extends IBaseInput {
  componentType?: LoanPurposeComponentTypes;
  purpose?: string;
  additionalInfo?: string;
  additionalInfoLabel?: string;
  dropDownValues?: IDropDownOption[];
}

interface ILoanPurposeProps extends ILoanPurpose, IBaseComponent {}

const LoanPurpose: FC<ILoanPurposeProps> = (props) => {
  const dispatch = useDispatch();

  const value = useSelector((state: IState) => state.onboarding.loanPurpose);
  const error = useSelector((state: IState) => state.error.loanPurpose?.missing);
  const { locale } = useSelector((state: IState) => state.widgetContext);
  const loanPurpose = value?.purpose || '';
  const additionalInfo = value?.additionalInfo || '';

  const { label, additionalInfoLabel, dropDownValues } = getLoanPurposeLabels(locale);
  const componentLabel = props.label || label;

  const onChangePurpose = (event: ChangeEvent<any>) => {
    const inputValue = event.target.value;
    dispatch(setLoanPurpose(inputValue));
    dispatch(
      pushToGTM({
        event: 'q_user_a_loan_purpose_select',
      })
    );
    if (error) dispatch(setMissingError(LOAN_PURPOSE, ''));
  };

  const onChangeAdditional = (event: ChangeEvent<any>) => {
    const inputValue = event.target.value;
    dispatch(setLoanPurposeAdditionalInfo(inputValue));
    if (error) dispatch(setMissingError(LOAN_PURPOSE, ''));
  };

  switch (props.componentType) {
    case LoanPurposeComponentTypes.dropDown:
      return (
        <>
          <DropDown
            {...props}
            label={componentLabel}
            value={loanPurpose}
            dropDownValues={props.dropDownValues || dropDownValues}
            onChange={onChangePurpose}
            analyticsEvent="loan-purpose"
          />
          <Input
            {...props}
            label={props.additionalInfoLabel || additionalInfoLabel}
            onChange={onChangeAdditional}
            value={additionalInfo}
            analyticsEvent="loan-purpose"
          />
          {error && <ComponentError error={error} />}
        </>
      );
    case LoanPurposeComponentTypes.textArea:
      return <TextArea {...props} onChange={onChangePurpose} value={loanPurpose} error={error} />;
    default:
      return (
        <Input
          {...props}
          label={componentLabel}
          onChange={onChangePurpose}
          value={loanPurpose}
          error={error}
          analyticsEvent="loan-purpose"
        />
      );
  }
};

export default LoanPurpose;
